import React, { useState, useCallback, useEffect } from "react";
import locale from "localization";
import styles from "./station-details.module.scss";
import {
  Field,
  TextField,
  Title,
  Button,
  Text,
  SearchField,
  Dropdown,
  Autocomplete,
} from "components/commons";
import { prettifyProductCode, prettifyProductCodeStyle } from "utils/pretty.utils";
import StationMap from "components/commons/station-map-list/station-map";
import { useMount } from "hooks";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import {
  STATION_TYPE_OPTIONS,
  ISLAND_OPTIONS,
  PROVINCES_BY_ISLAND,
  PRODUCT_OPTIONS,
  ALL_PRODUCT_CODES,
} from "./station-constants";

const StationDetailsFormModule = ({ ...stateForm }) => {
  const {
    viewMode,
    addMode,
    editMode,
    fields,
    modifyField,
    modifyForm,
    loading,
    handleSubmit,
    formValid,
    setIsValidCoordinates,
    products,
    setProducts,
  } = stateForm;

  const [currentLocation, setCurrentLocation] = useState(null);
  const [allowAccessLocation, setAllowAccessLocation] = useState(true);
  const [hasSearchLocation, setHasSearchLocation] = useState(false);

  const getCurrentPosition = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (position) {
          setCurrentLocation({ lat: 0, lng: 0 });
        }
      },
      () => setAllowAccessLocation(false)
    );
  }, []);

  useMount(() => {
    getCurrentPosition();
  });

  useEffect(() => {
    if (viewMode) setHasSearchLocation(true);
  }, [viewMode]);

  useEffect(() => {
    if (fields?.stationLocation?.value) setHasSearchLocation(true);
  }, [fields?.stationLocation?.value]);

  const applyEnter = useCallback(() => {
    setHasSearchLocation(true);
  }, []);

  const handleMapClick = useCallback(
    (lat, lng) => {
      if (viewMode) return;
      modifyField(fields?.stationLocation?.name, { value: `${lat.toFixed(6)}, ${lng.toFixed(6)}` });
      setIsValidCoordinates(true);
      setHasSearchLocation(true);
    },
    [viewMode, modifyField, fields?.stationLocation?.name, setIsValidCoordinates]
  );

  // Province options filtered by selected island
  const provinceOptions = (PROVINCES_BY_ISLAND[fields?.island?.value] || []).map((p) => ({
    label: p,
    value: p,
  }));

  // Product dropdown options: all products minus already selected (except current row)
  const getProductOptionsForRow = useCallback(
    (currentCode) => {
      return PRODUCT_OPTIONS.filter(
        (opt) => !products.includes(opt.value) || opt.value === currentCode
      );
    },
    [products]
  );

  const updateProduct = useCallback(
    (index, newCode) => {
      setProducts((prev) => {
        const next = [...prev];
        next[index] = newCode;
        return next;
      });
    },
    [setProducts]
  );

  const removeProduct = useCallback(
    (index) => {
      setProducts((prev) => prev.filter((_, i) => i !== index));
    },
    [setProducts]
  );

  const addProduct = useCallback(() => {
    const next = ALL_PRODUCT_CODES.find((code) => !products.includes(code));
    if (next) setProducts((prev) => [...prev, next]);
  }, [products, setProducts]);

  const detailsMapProps = {
    currentLocation,
    allowAccessLocation,
    hasSearchLocation,
    fields,
    setIsValidCoordinates,
    modifyField,
    onMapClick: handleMapClick,
    viewMode,
  };

  return (
    <div className={styles.container}>
      <Title black xsmall>
        {locale.basicInformation}
      </Title>

      <Field className={styles.fieldLabel} {...fields?.stationCode}>
        {viewMode || editMode ? (
          <Text className={styles.fieldText} style={{ color: "black" }}>
            {fields?.stationCode?.value}
          </Text>
        ) : (
          <TextField
            maxLength={4}
            {...fields?.stationCode}
            onChange={(name, { value }) => {
              const filtered = value.replace(/^0+/, "").replace(/\D/g, "").slice(0, 4);
              modifyField(name, { value: filtered });
            }}
            data-testid="st-field-station-code"
          />
        )}
      </Field>

      <Field className={styles.fieldLabel} {...fields?.stationName}>
        {viewMode || editMode ? (
          <Text className={styles.fieldText}>{fields?.stationName?.value}</Text>
        ) : (
          <TextField
            maxLength={100}
            {...fields?.stationName}
            onChange={(name, { value }) =>
              modifyField(name, { value: value.slice(0, 100) })
            }
          />
        )}
      </Field>

      <Field className={styles.fieldLabel} {...fields?.businessName}>
        {viewMode || editMode ? (
          <Text className={styles.fieldText}>{fields?.businessName?.value}</Text>
        ) : (
          <TextField
            maxLength={100}
            {...fields?.businessName}
            onChange={(name, { value }) =>
              modifyField(name, { value: value.slice(0, 100) })
            }
          />
        )}
      </Field>

      <Field className={styles.fieldLabel} {...fields?.customerCode}>
        {viewMode || editMode ? (
          <Text className={styles.fieldText}>{fields?.customerCode?.value}</Text>
        ) : (
          <TextField maxLength={5} {...fields?.customerCode} onChange={modifyField} />
        )}
      </Field>

      <Field className={styles.fieldLabel} {...fields?.stationType}>
        {viewMode || editMode ? (
          <Text className={styles.fieldText}>{fields?.stationType?.value}</Text>
        ) : (
          <Dropdown
            value={fields?.stationType?.value || "none"}
            options={STATION_TYPE_OPTIONS}
            placeholder={locale.selectType}
            onChange={(v) => modifyField("stationType", { value: v })}
          />
        )}
      </Field>

      <Field className={styles.fieldLabel} {...fields?.island}>
        {viewMode || editMode ? (
          <Text className={styles.fieldText}>{(fields?.island?.value || "").toUpperCase()}</Text>
        ) : (
          <Dropdown
            value={fields?.island?.value || "none"}
            options={ISLAND_OPTIONS}
            placeholder={locale.selectIsland}
            onChange={(v) => modifyForm({ island: { value: v }, province: { value: "" } })}
          />
        )}
      </Field>

      <Field className={styles.fieldLabel} {...fields?.province}>
        {viewMode || editMode ? (
          <Text className={styles.fieldText}>{fields?.province?.value}</Text>
        ) : (
          <Dropdown
            value={fields?.province?.value || "none"}
            options={provinceOptions}
            placeholder={locale.selectProvince}
            disabled={false}
            onChange={(v) => modifyField("province", { value: v })}
          />
        )}
      </Field>

      {/* ── Products ── */}
      <Title black xsmall>
        {locale.products}
      </Title>

      {viewMode ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
          {products.map((code) => (
            <div key={code} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div className={styles.productDot} style={{ ...prettifyProductCodeStyle(code) }} />
              <Text className={styles.fieldText}>{prettifyProductCode(code)}</Text>
            </div>
          ))}
        </div>
      ) : (
        <>
          {products.map((code, i) => (
            <div key={i} className={styles.productRow} data-testid={`st-product-row-${code}`}>
              <Text className={styles.productLabel}>
                {locale.product} {i + 1}
              </Text>

              <div className={styles.productSelect}>
                <div className={styles.productDot} style={{ ...prettifyProductCodeStyle(code) }} />
                <Autocomplete
                  name={`product-${i}`}
                  value={code}
                  options={getProductOptionsForRow(code)}
                  placeholder={locale.product}
                  onChange={(_, { value }) => {
                    if (value) updateProduct(i, value);
                  }}
                  className={styles.productDropdown}
                />
              </div>
              <CloseIcon className={styles.removeProduct} onClick={() => removeProduct(i)} />
            </div>
          ))}

          <Button
            outline
            className={styles.addProductBtn}
            startIcon={<AddIcon />}
            onClick={addProduct}
            disabled={products.length >= ALL_PRODUCT_CODES.length}
            data-testid="st-add-product-btn"
          >
            Add Product
          </Button>
        </>
      )}

      {/* ── Location ── */}
      <Title black xsmall>
        {locale.location}
      </Title>

      {!viewMode && (
        <Text label style={{ marginBottom: "20px" }}>
          {locale.locationDetails}
        </Text>
      )}

      {viewMode && (
        <div className={styles.tableStationDetails}>
          <div className={styles.row}>
            <label>{locale.longitudeLatitude}</label>
            <label>{fields?.stationLocation?.value}</label>
          </div>
        </div>
      )}

      <div className={styles.parentMap}>
        <StationMap {...detailsMapProps} />
        {!viewMode && (
          <div className={styles.search}>
            <Field style={{ margin: 0 }} {...fields?.stationLocation}>
              <SearchField
                className={styles.inputFieldSearch}
                restrict={false}
                searchWithSymbols
                maxLength={100}
                placeholder={[locale.latitude, locale.longitude].join(" / ")}
                value={fields?.stationLocation?.value}
                onEnter={applyEnter}
                onClear={() => modifyField(fields?.stationLocation?.name, { value: "" })}
                onChange={(_, { value }) =>
                  modifyField(fields?.stationLocation?.name, { value: value.slice(0, 100) })
                }
              />
            </Field>
          </div>
        )}
      </div>

      {addMode && (
        <Button
          primary
          className={styles.continue}
          loading={loading}
          disabled={!formValid}
          onClick={handleSubmit}
        >
          {locale.continue}
        </Button>
      )}
    </div>
  );
};

export default StationDetailsFormModule;
